import {
  platformFeatures,
  POST_TYPE_KEY,
  POST_TYPE_TRIAL_REEL_KEY
} from '../config/platformFeatures';
import { GRADUATION_STRATEGY_MANUAL } from '../config/constants';
import { Dropdown } from 'react-bootstrap';
import { useTranslate } from '@tolgee/react';
import { useDispatch, useSelector } from 'react-redux';
import { useRef, useState } from 'react';
import {
  setPostDataAttribute,
  updatePostTypeSelected
} from '../../../redux/slices/lomavisCreator/lomavisCreator';
import { useClickAway } from 'react-use';
import { lomavisCreatorRootState } from '../../../pages/lomavisCreator';
import { validateUpload } from '../config/postValidator';
import toast from 'react-hot-toast';
import { getObjectAsFile } from '../utils/helpers';
import { INSTAGRAM } from '@/config/constants';

const PostTypeDropDown: React.FC<any> = ({ platform }) => {
  const dispatch = useDispatch();
  const postTypeSelected = useSelector(
    (state: any) => state.lomavisCreator.postTypeSelected
  );

  const generalPostType = useSelector(
    (state: any) => state.lomavisCreator.general_post_type
  );

  const postTypeMediaArray = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData?.[platform]?.media
  );
  const postData = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData?.[platform]
  );
  const ref = useRef(null);

  const [showDropdown, setShowDropdown] = useState(false);
  const { t } = useTranslate();
  useClickAway(ref, () => {
    setShowDropdown(false);
  });

  const resolvedGeneralPostType = generalPostType || POST_TYPE_KEY;

  // Prefer the array defined under the general post type key (gallery/template/etc.).
  // If the general post type is actually a concrete typeKey (e.g., story/reel) inside POST_TYPE_KEY,
  // return only that entry. Fallback to the default POST_TYPE_KEY list.
  const generalPostTypeArray = platformFeatures[platform]?.[
    resolvedGeneralPostType
  ];
  const defaultPostTypeArray = platformFeatures[platform]?.[POST_TYPE_KEY] || [];

  const platformPostTypeOptions =
    Array.isArray(generalPostTypeArray) && generalPostTypeArray.length
      ? generalPostTypeArray
      : (() => {
          const concreteMatch = defaultPostTypeArray.find(
            (opt) => opt.typeKey === resolvedGeneralPostType
          );
          return concreteMatch ? [concreteMatch] : defaultPostTypeArray;
        })();

  const selectedTypeKey = platformPostTypeOptions.some(
    (option) => option.typeKey === postTypeSelected[platform]
  )
    ? postTypeSelected[platform]
    : platformPostTypeOptions[0]?.typeKey;

  const selectedOption =
    platformPostTypeOptions.find(
      (postType) => postType.typeKey === selectedTypeKey
    ) || platformPostTypeOptions[0];
  const selectedLabel = selectedOption?.title || '';

  return (
    <Dropdown
      onClick={() => setShowDropdown(!showDropdown)}
      show={showDropdown}
      ref={ref}
    >
      <Dropdown.Toggle
        variant="secondary"
        id="dropdown-basic"
        className={'badge badge-secondary bg-secondary'}
        style={{
          padding: '0.25em 0.4em',
          fontSize: '80%',
          fontWeight: 700,
          lineHeight: 1,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          verticalAlign: 'baseline',
          borderRadius: '0.25rem'
        }}
      >
        {t(selectedLabel)}
      </Dropdown.Toggle>

      <Dropdown.Menu className="w-250px">
        {platformPostTypeOptions.map((postType, index) => {
          return (
            <Dropdown.Item
              key={index}
              id={postType.typeKey}
              onClick={async () => {
                const is_valid = async () => {
                  try {
                    const validationState = validateUpload(
                      platform,
                      postType?.typeKey,
                      (postTypeMediaArray || []).map((item) =>
                        item?.file ? item?.file : getObjectAsFile(item)
                      ),
                      t
                    );

                    return validationState;
                  } catch (error) {
                    console.error('Validation error:', error.message);
                    return false; // or you could return an error object, or handle it however you need to
                  }
                };

                if (await is_valid()) {
                  dispatch(
                    updatePostTypeSelected({
                      platform,
                      postType: postType.typeKey
                    })
                  );
                  if (postType.typeKey === POST_TYPE_TRIAL_REEL_KEY) {
                    dispatch(
                      setPostDataAttribute({
                        platform,
                        attribute: 'additional_publication_data',
                        value: {
                          ...(postData?.additional_publication_data || {}),
                          instagram_graduation_strategy:
                            GRADUATION_STRATEGY_MANUAL,
                          instagram_trial_reel_enabled: true
                        }
                      })
                    );
                  } else if (platform === INSTAGRAM) {
                    // clear trial flags when switching away
                    const {
                      instagram_graduation_strategy,
                      instagram_trial_reel_enabled,
                      ...rest
                    } = postData?.additional_publication_data || {};
                    dispatch(
                      setPostDataAttribute({
                        platform,
                        attribute: 'additional_publication_data',
                        value: rest
                      })
                    );
                  }
                } else {
                  toast.error(`${t('lomavisCreator.errors.postTypeChange')}`, {
                    duration: 4000,
                    position: 'top-right'
                  });
                }
              }}
            >
              <div className="d-flex flex-column">
                <div className="fw-bolder">{t(postType.title)}</div>
                <div className="fs-7 fw-semibold text-muted text-wrap">
                  {t(postType.description)}
                </div>
              </div>
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default PostTypeDropDown;
